import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import {
  Plus,
  Search,
  Building2,
  Loader2,
  Mail,
  Phone,
  Trash2,
  ScrollText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { NewCariDialog, type CariType } from '@/components/trading/cari/new-cari-dialog';
import { CariCreateModals } from '@/components/trading/cari/cari-create-modals';
import {
  cariTypeDisplay,
  cariTypeLabel,
  isGenericCariType,
} from '@/components/trading/cari/cari-type-display';
import { cn } from '@/lib/utils';

export function CariKartlarPage() {
  const { t, i18n } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canCreateCustomer = usePermission('CUSTOMERS', 'CREATE', 'TRADING');
  const canCreateSupplier = usePermission('SUPPLIERS', 'CREATE', 'TRADING');
  const canCreate = canCreateCustomer || canCreateSupplier;
  const canDelete = usePermission('CUSTOMERS', 'DELETE', 'TRADING');

  const initialType = searchParams.get('type') as CariType | null;
  const [activeTab, setActiveTab] = useState<CariType | null>(initialType);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  // New cari dialog
  const [newCariOpen, setNewCariOpen] = useState(false);
  // Type whose creation modal is open (dedicated or generic)
  const [createType, setCreateType] = useState<CariType | null>(null);
  // Delete
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; source: string; name: string } | null>(null);

  const utils = trpc.useUtils();

  const deleteMutation = trpc.tradingCari.delete.useMutation({
    onSuccess: () => {
      utils.tradingCari.list.invalidate();
      utils.tradingCari.counts.invalidate();
      showSuccessToast(tCommon('deleted'));
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleDelete = (e: React.MouseEvent, item: { id: string; source: string; companyName: string }) => {
    e.stopPropagation();
    setItemToDelete({ id: item.id, source: item.source, name: item.companyName });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (itemToDelete) {
      deleteMutation.mutate({
        id: itemToDelete.id,
        source: itemToDelete.source as 'customer' | 'supplier',
      });
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const handleTabChange = (tab: CariType | null) => {
    setActiveTab(tab);
    setPage(1);
    if (tab) {
      setSearchParams({ type: tab });
    } else {
      setSearchParams({});
    }
  };

  const { data, isLoading } = trpc.tradingCari.list.useQuery({
    search: debouncedSearch || undefined,
    cariType: activeTab || undefined,
    page,
    pageSize: 24,
  });

  const { data: counts } = trpc.tradingCari.counts.useQuery();
  const { data: cariTypes } = trpc.tradingCariType.list.useQuery();

  const typeByCode = new Map((cariTypes ?? []).map((tp) => [tp.code, tp]));
  const labelFor = (code: string): string => {
    const row = typeByCode.get(code);
    return row ? cariTypeLabel(row, i18n.language) : code;
  };

  const handleNewCariSelect = (type: CariType) => {
    setCreateType(type);
  };

  const handleCardClick = (item: { id: string; source: string; cariType: string }) => {
    const cariType = item.cariType as CariType;
    switch (cariType) {
      case 'M':
        navigate(`/trading/customers/${item.id}`);
        break;
      case 'S':
        navigate(`/trading/cari/sellers/${item.id}`);
        break;
      case 'T':
        navigate(`/trading/cari/suppliers/${item.id}`);
        break;
      case 'B':
        navigate(`/trading/cari/dealers/${item.id}`);
        break;
      default:
        if (isGenericCariType(cariType)) {
          navigate(`/trading/cari/generic/${cariType}/${item.id}`);
        }
    }
  };

  const items = data?.items ?? [];
  const pagination = data?.pagination;

  const tabs: { key: CariType | null; label: string; count?: number }[] = [
    { key: null, label: t('cari.allTypes'), count: counts?.total },
    ...(cariTypes ?? []).map((tp) => ({
      key: tp.code as CariType,
      label: cariTypeLabel(tp, i18n.language),
      count: counts?.byType[tp.code] ?? 0,
    })),
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('cari.title')}</h1>
          <p className="text-muted-foreground">
            {t('cari.subtitle')}
            {pagination && ` (${pagination.total} ${tCommon('total')})`}
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setNewCariOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('cari.newCari')}
          </Button>
        )}
      </div>

      {/* Modals */}
      <NewCariDialog
        open={newCariOpen}
        onOpenChange={setNewCariOpen}
        onSelect={handleNewCariSelect}
      />
      <CariCreateModals
        createType={createType}
        typeLabel={createType ? labelFor(createType) : undefined}
        onClose={() => {
          setCreateType(null);
          utils.tradingCari.list.invalidate();
          utils.tradingCari.counts.invalidate();
        }}
      />

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={tCommon('delete')}
        description={t('cari.deleteConfirm')}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key ?? 'all'}
            onClick={() => handleTabChange(tab.key)}
            className={cn(
              'px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap',
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-muted rounded-full">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('cari.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-8 w-full border border-border bg-background pl-10 pr-4 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20"
          />
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">{tCommon('loading')}</span>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && items.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Building2 className="h-12 w-12 text-muted-foreground/50" />
          <h3 className="mt-4 text-lg font-medium">{t('cari.noCari')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {debouncedSearch ? t('cari.noCariSearch') : t('cari.noCariDesc')}
          </p>
          {!debouncedSearch && canCreate && (
            <Button className="mt-4" onClick={() => setNewCariOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('cari.newCari')}
            </Button>
          )}
        </div>
      )}

      {/* Cards Grid */}
      {!isLoading && items.length > 0 && (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => {
              const config = cariTypeDisplay(item.cariType);
              const Icon = config.icon;

              return (
                <div
                  key={`${item.source}-${item.id}`}
                  className="group border border-border bg-card p-4 transition-shadow hover:shadow-md cursor-pointer"
                  onClick={() => handleCardClick(item)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`flex h-10 w-10 items-center justify-center ${config.bgColor}`}>
                        <Icon className={`h-5 w-5 ${config.iconColor}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold">{item.companyName}</h3>
                        {item.tradeName && (
                          <p className="text-sm text-muted-foreground">{item.tradeName}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/trading/cari/${item.source}/${item.id}/movements`);
                        }}
                        className="p-1.5 text-muted-foreground hover:text-primary"
                        title={t('cariMovements.viewLedger', 'Ledger')}
                      >
                        <ScrollText className="h-4 w-4" />
                      </button>
                      {canDelete && (
                        <button
                          onClick={(e) => handleDelete(e, item)}
                          className="p-1.5 text-muted-foreground hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <span className={`px-2 py-0.5 text-xs font-medium ${config.badgeColor}`}>
                      {item.cariType} - {labelFor(item.cariType)}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium ${
                        item.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {item.isActive ? tCommon('active') : tCommon('inactive')}
                    </span>
                  </div>

                  <div className="mt-3 space-y-1.5 text-sm">
                    {item.contactName && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <span className="font-medium">{item.contactName}</span>
                      </div>
                    )}
                    {item.email && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="h-3.5 w-3.5" />
                        <span className="truncate">{item.email}</span>
                      </div>
                    )}
                    {item.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-3.5 w-3.5" />
                        <span>{item.phone}</span>
                      </div>
                    )}
                  </div>

                  {(item.city || item.country) && (
                    <div className="mt-3 border-t pt-3 text-xs text-muted-foreground">
                      {[item.city, item.country].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                {tCommon('previous')}
              </Button>
              <span className="text-sm text-muted-foreground">
                {t('cari.pageOf', { current: page, total: pagination.totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page === pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                {tCommon('next')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
